import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useT } from "../i18n/I18nProvider";
import { fetchSources } from "../lib/api";
import DataErrorNotice from "../components/DataErrorNotice";
import { ExternalLink, CalendarClock, ShieldAlert, FileSearch, Repeat, Info } from "lucide-react";

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("fr-FR", { year: "numeric", month: "long", day: "numeric" });
  } catch { return iso; }
}

export default function Methodology() {
  const { t } = useT();
  const [sources, setSources] = useState([]);
  const [err, setErr] = useState(false);

  useEffect(() => {
    fetchSources().then(setSources).catch((e) => { console.error("[Methodology] sources failed:", e?.message); setErr(true); });
  }, []);

  return (
    <div className="max-w-3xl mx-auto px-4 py-16 md:py-24" data-testid="methodology-page">
      <span className="hej-chip"><span className="w-1.5 h-1.5 rounded-full bg-[#DC143C]"></span>{t("methodology.chip")}</span>
      <h1 className="font-display text-4xl sm:text-5xl font-bold text-slate-900 tracking-tight mt-4 mb-6">
        {t("methodology.title")}
      </h1>
      <p className="text-slate-600 leading-relaxed text-lg">{t("methodology.intro")}</p>

      <section className="mt-12">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-[#0056D2]/10 flex items-center justify-center flex-shrink-0">
            <FileSearch className="w-5 h-5 text-[#0056D2]" />
          </div>
          <div>
            <h2 className="font-display text-xl font-semibold text-slate-900">{t("methodology.howTitle")}</h2>
            <p className="text-slate-600 mt-2 leading-relaxed">{t("methodology.howBody")}</p>
          </div>
        </div>
      </section>

      <section className="mt-10">
        <h2 className="font-display text-xl font-semibold text-slate-900 mb-4">{t("methodology.sourcesTitle")}</h2>
        {err && <DataErrorNotice />}
        <div className="space-y-4">
          {sources.map((s) => (
            <div key={s.id} className="hej-card p-5" data-testid={`source-${s.id}`}>
              <h3 className="font-display font-semibold text-slate-900">{s.label_fr}</h3>

              {s.methodology && (
                <div className="mt-3 flex items-start gap-2 text-sm text-slate-600 bg-slate-50 rounded-lg p-3">
                  <Info className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" />
                  <p>{s.methodology}</p>
                </div>
              )}

              <div className="mt-4">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">{t("methodology.referenced")}</p>
                <div className="flex flex-wrap gap-2">
                  {(s.urls || []).map((u, i) => (
                    <a key={i} href={u.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-slate-200 hover:border-[#0056D2] hover:bg-[#0056D2]/5 text-xs font-medium text-slate-700 hover:text-[#0056D2] transition" data-testid={`source-link-${s.id}-${i}`}>
                      {u.name} <ExternalLink className="w-3 h-3" />
                    </a>
                  ))}
                </div>
              </div>

              <div className="mt-4 flex flex-wrap gap-x-5 gap-y-1 text-xs text-slate-500">
                <span className="flex items-center gap-1.5"><Repeat className="w-3 h-3" />{s.frequency}</span>
                <span className="flex items-center gap-1.5"><CalendarClock className="w-3 h-3" />{t("common.lastUpdated")} : {formatDate(s.last_updated)}</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-10">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center flex-shrink-0">
            <CalendarClock className="w-5 h-5 text-amber-700" />
          </div>
          <div>
            <h2 className="font-display text-xl font-semibold text-slate-900">{t("methodology.frequencyTitle")}</h2>
            <p className="text-slate-600 mt-2 leading-relaxed">{t("methodology.frequencyBody")}</p>
          </div>
        </div>
      </section>

      <section className="mt-10 bg-amber-50 border border-amber-200 rounded-2xl p-6">
        <div className="flex items-start gap-3">
          <ShieldAlert className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
          <div>
            <h2 className="font-display text-xl font-semibold text-amber-900">{t("methodology.disclaimerTitle")}</h2>
            <p className="text-amber-900 mt-2 leading-relaxed text-sm">{t("methodology.disclaimerBody")}</p>
          </div>
        </div>
      </section>

      <div className="mt-10">
        <Link to="../" className="hej-link">← Retour à l'accueil</Link>
      </div>
    </div>
  );
}
